import { Button, Image } from 'react-bootstrap'
import { FaCheckCircle } from 'react-icons/fa'
import clsx from 'clsx'
import { Link, useHistory } from 'react-router-dom'

import useContainerContext from '../../containerContext'
import { Tooltip } from '../../controls'
import { SortableColumn } from '../../table-plugins/sortable'
import Table from '../../table'
import { formatToLocalTime } from '../../../utils/dates'
import { numberFormatCurrency } from '../../../utils/helpers'
import DownloadPhotos from '../../../assets/images/icons/photo-download.svg'
import DownloadPhotosDisabled from '../../../assets/images/icons/photo-downloaddisabled.svg'
import { downloadPhotosRecord, getPhotos } from '../../../utils/invoice'
import AvatarName from '../../avatarName'
import useAppContext from '../../app'
import VehicleSummary from '../../vehicle/VehicleSummary'
import { getVehicles, summarizeVehicles } from '../../../utils/vehicleSummary'
import './estimates.css'

const TableDesktop = () => {
	const { data = [], state, setState } = useContainerContext()
	const history = useHistory()
	const { setIsLoading } = useAppContext()
	return (
		<Table
			data={data}
			rowKey='_id'
			onRowClick={row => history.push(`/estimates/${row._id}`)}
			state={state}
			setState={setState}
			className='primary'
			borderless
		>
			<SortableColumn
				dataKey='dateCreated'
				width='10%'
				headerLabel={
					<u className='d-inline-block me-1'>
						<Tooltip
							placement='bottom'
							message={
								<span>
									{`Click to sort by date
									ascending or descending`}
								</span>
							}
						>
							Date
						</Tooltip>
					</u>
				}
				className='text-start'
				render={value => formatToLocalTime(value)}
			/>
			<SortableColumn
				dataKey='company.name'
				headerLabel={
					<u className='d-inline-block me-1'>
						<Tooltip
							placement='bottom'
							message={
								<span>
									{`Click to sort alphabetically
									ascending or descending`}
								</span>
							}
						>
							Vendor
						</Tooltip>
					</u>
				}
				render={(value, { rowData }) => {
					const { client = {} } = rowData
					const active = client?._id?.group?.companyOverride?.active
					const name = client?._id?.group?.companyOverride?.name
					return (
						<div className='d-flex align-items-center'>
							<AvatarName
								size='medium'
								value={active ? name : value}
								className='me-2'
							/>
							<div>{active ? name : value}</div>
						</div>
					)
				}}
			/>

			<SortableColumn
				dataKey='number'
				width='9%'
				headerLabel={
					<u className='d-inline-block me-1'>
						<Tooltip
							placement='bottom'
							message={
								<span>Click to sort by number ascending or descending</span>
							}
						>
							Estimate #
						</Tooltip>
					</u>
				}
				className='text-start'
				render={(number, { rowData: { _id } }) => {
					return (
						<Link to={`/estimates/${_id}`} className='text-primary'>
							#{number}
						</Link>
					)
				}}
			/>
			<Table.Column
				id='vehicleInfo'
				dataKey='vehicles'
				width='30%'
				header='Vehicle Info'
				className='text-center'
				render={(_, { rowData }) => {
					const { vehicle, extraCount } = summarizeVehicles(getVehicles(rowData))
					return <VehicleSummary vehicle={vehicle} extraCount={extraCount} />
				}}
			/>
			<SortableColumn
				dataKey='total'
				width='10%'
				headerLabel={
					<u className='d-inline-block me-1'>
						<Tooltip
							placement='bottom'
							message={
								<span>{`Click to sort by dollar amount high or low`}</span>
							}
						>
							Total
						</Tooltip>
					</u>
				}
				className='text-start'
				render={(value, { rowData }) => {
					const currency = rowData.company?.currency || 'usd'
					const depositFlat = rowData.depositFlat || 0
					const paid = rowData.payment?.amount || 0
					// Round before the > 0 test: a settled deposit can leave float dust
					// (e.g. 2.84e-14) that would otherwise render "$0.00 Deposit Due".
					const depositDue = Math.round((depositFlat - paid) * 100) / 100
					return (
						<>
							<div>{numberFormatCurrency(currency, value || 0)}</div>
							{depositDue > 0 && (
								<span className='deposit-due-pill'>
									{`${numberFormatCurrency(currency, depositDue)} Deposit Due`}
								</span>
							)}
						</>
					)
				}}
			/>
			<Table.Column
				id='photos'
				dataKey='_id'
				width='9%'
				header='Photos'
				className='text-center'
				render={(_id, { rowData }) => {
					const isPhotos = getPhotos(rowData)
					return (
						<Button
							variant='default'
							onClick={() => {
								if (isPhotos.length > 0) {
									setIsLoading(true)
									downloadPhotosRecord(rowData, () => {
										setIsLoading(false)
									})
								}
							}}
						>
							<Image
								className='pointer'
								src={
									isPhotos.length > 0 ? DownloadPhotos : DownloadPhotosDisabled
								}
								alt=''
							/>
						</Button>
					)
				}}
			/>
			<SortableColumn
				dataKey='status'
				width='8%'
				headerLabel={<u className='d-inline-block me-1'>Approved?</u>}
				className='text-center'
				render={(totalApproved = 0, { rowData }) => {
					const { status } = rowData

					return (
						<FaCheckCircle
							className={clsx({
								'text-success': !status,
								'text-gray': status,
							})}
						/>
					)
				}}
			/>
		</Table>
	)
}

export default TableDesktop
